import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Inbox, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Per-case "forward agent replies here" address card.
 * - Fetches /api/cases/{caseId}/inbound-address
 * - One-click copy
 * - Two visual variants:
 * variant="compact". minimal one-line strip, fits inside the case header
 * variant="full". expanded card with instructions, for the Letters tab
 */
export default function InboundForwardCard({ caseId, variant = "full" }) {
 const [address, setAddress] = useState("");
 const [instructions, setInstructions] = useState("");
 const [copied, setCopied] = useState(false);
 const [expanded, setExpanded] = useState(false);

 useEffect(() => {
 if (!caseId) return;
 api.get(`/cases/${caseId}/inbound-address`)
.then(({ data }) => {
 setAddress(data.address || "");
 setInstructions(data.instructions || "");
 })
.catch(() => {});
 }, [caseId]);

 const copy = () => {
 if (!address) return;
 navigator.clipboard.writeText(address);
 setCopied(true);
 setTimeout(() => setCopied(false), 1500);
 };

 if (!address) return null;

 if (variant === "compact") {
 return (
 <div
 className="mt-4 border border-[#1B6C62]/25 bg-[#E6F5F2]/50 px-3 py-2 flex items-center gap-2 flex-wrap"
 data-testid="inbound-forward-compact"
 >
 <Inbox size={14} strokeWidth={1.75} className="text-[#1B6C62] shrink-0" />
 <span className="text-xs text-[#0D2B52] font-serif">Forward agent replies to</span>
 <code
 className="font-mono text-xs text-[#0D2B52] bg-white border border-[#E5E4DE] px-2 py-0.5 break-all"
 data-testid="inbound-forward-address"
 >
 {address}
 </code>
 <button
 onClick={copy}
 className="btn-ghost text-xs inline-flex items-center gap-1 ml-auto"
 data-testid="inbound-forward-copy"
 >
 {copied ? (
 <><Check size={12} strokeWidth={2} /> Copied</>
 ) : (
 <><Copy size={12} strokeWidth={1.5} /> Copy</>
 )}
 </button>
 <button
 onClick={() => setExpanded(v => !v)}
 className="btn-ghost text-xs inline-flex items-center gap-1"
 data-testid="inbound-forward-toggle"
 aria-label="Toggle instructions"
 >
 {expanded ? <ChevronUp size={12} strokeWidth={1.75} /> : <ChevronDown size={12} strokeWidth={1.75} />}
 </button>
 {expanded && (
 <p className="basis-full text-[11px] text-[#6B7280] font-serif italic leading-snug pt-1">
 {instructions}
 </p>
 )}
 </div>
 );
 }

 // full variant. for Letters tab
 return (
 <div className="card bg-[#E6F5F2]/40" data-testid="inbound-forward-card">
 <div className="flex items-start gap-3">
 <Inbox size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5 shrink-0" />
 <div className="flex-1 min-w-0">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Forward agent replies to TenantTrail</h3>
 <p className="text-sm text-[#333] leading-relaxed mb-3">
 {instructions}
 </p>
 <div className="flex items-center gap-2 flex-wrap">
 <code
 className="font-mono text-sm text-[#0D2B52] bg-white border border-[#E5E4DE] px-3 py-1.5 break-all flex-1 min-w-0"
 data-testid="inbound-forward-address"
 >
 {address}
 </code>
 <button
 onClick={copy}
 className="btn-primary text-xs inline-flex items-center gap-1.5"
 data-testid="inbound-forward-copy"
 >
 {copied ? (
 <><Check size={13} strokeWidth={2} /> Copied</>
 ) : (
 <><Copy size={13} strokeWidth={1.5} /> Copy address</>
 )}
 </button>
 </div>
 <p className="text-[11px] text-[#8A6D3B] italic font-serif leading-relaxed mt-3">
 Tip: in Gmail, hit <strong>Forward</strong> on the agent&apos;s email and paste this address. Keep the original headers so TenantTrail can preserve the send date.
 </p>
 </div>
 </div>
 </div>
 );
}
